import type { JSX } from "react"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router"

import { datadogRum } from "@datadog/browser-rum"

import {
  Badge,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  styled,
} from "@mui/material"
import type { BadgeProps, ListItemButtonProps } from "@mui/material"

const IconBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    boxShadow: "0px 1px 3px 0px rgba(0,0,0,0.05)",
    fontSize: "0.55rem",
    fontWeight: theme.typography.fontWeightBold,
    height: "14px",
    padding: "0 5px",
    right: "-14px",
    transform: "translate(0, -50%)",
    whiteSpace: "nowrap",
  },
}))

const TextBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    boxShadow: "0px 1px 3px 0px rgba(0,0,0,0.05)",
    fontSize: "0.55rem",
    fontWeight: theme.typography.fontWeightBold,
    height: "14px",
    left: "-17px",
    marginLeft: "6px",
    padding: "0 5px",
    position: "relative",
    top: "11px",
  },
}))

interface NavItemButtonProps extends ListItemButtonProps {
  dataE2e?: string
  from?: string
  icon: JSX.Element
  isBeta?: boolean
  isOpen: boolean
  title: string
  to: string
}

export const NavItemButton = ({
  dataE2e,
  from,
  icon,
  isBeta,
  isOpen,
  id,
  title,
  to,
}: NavItemButtonProps) => {
  const { t } = useTranslation()
  const label = isBeta ? `${title} (${t("beta", { ns: "common" })})` : title

  return (
    <ListItem key={title} disablePadding sx={{ display: "block" }}>
      <Tooltip
        disableHoverListener={isOpen}
        placement="right"
        title={!isOpen ? title : ""}
      >
        <ListItemButton
          aria-label={label}
          component={NavLink}
          data-e2e={dataE2e}
          id={id}
          onClick={() => {
            datadogRum.addAction("navigate", {
              description: `main nav: ${title}`,
              from: from,
              to: to,
            })
          }}
          sx={(theme) => ({
            color: "inherit",
            minHeight: 48,
            justifyContent: isOpen ? "initial" : "center",
            "&.active": {
              backgroundColor: `rgba(from ${theme.palette.primary.main} r g b / 0.54)`,
              color: "inherit",
              ":hover": {
                backgroundColor: `rgba(from ${theme.palette.primary.main} r g b / 0.62)`,
              },
            },
            "&.MuiListItemButton-root:hover:not(.active)": {
              backgroundColor: `rgba(from ${theme.palette.common.white} r g b / ${theme.palette.action.hoverOpacity})`,
            },
            "&.MuiListItemButton-root.Mui-focusVisible:not(.active)": {
              backgroundColor: `rgba(from ${theme.palette.common.white} r g b / ${theme.palette.action.focusOpacity})`,
            },
          })}
          to={to}
        >
          <IconBadge
            badgeContent={t("beta", { ns: "common" })}
            color="primary"
            invisible={!isBeta || isOpen}
          >
            <ListItemIcon
              sx={{
                color: (theme) => theme.palette.common.white,
                minWidth: 0,
                mr: isOpen ? 1 : "auto",
                justifyContent: "center",
              }}
            >
              {icon}
            </ListItemIcon>
          </IconBadge>
          {isOpen ? (
            <TextBadge
              badgeContent={t("beta", { ns: "common" })}
              color="primary"
              invisible={!isBeta}
            >
              <ListItemText
                primary={<Typography variant="body2">{title}</Typography>}
              />
            </TextBadge>
          ) : null}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  )
}
